import * as telephonyService from '@/services/phone-number.service';

import { queryKeys } from '@/query/query-keys';
import {
  notifyMutationErrorWithOptions,
  notifyMutationSuccessWithOptions,
} from '@/query/query-notifications';

import type { AssignAgentFormValues } from '@/schemas/phone-number.schema';

import type { PaginatedResponse, QueryOptions } from '@/types/common.type';
import type { PhoneNumberConfiguration, PhoneNumberFilterParams } from '@/types/phone-number.type';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const usePhoneNumberCreateMutation = (options?: QueryOptions<null>) => {
  return useMutation({
    mutationKey: queryKeys.phoneNumbers.mutations.create,
    mutationFn: telephonyService.createPhoneNumber,
    onSuccess: () => {
      notifyMutationSuccessWithOptions(
        options,
        'Phone number created',
        'A new phone number was added successfully.'
      );
      options?.onSuccess?.(null);
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Phone number creation failed', error);
      options?.onError?.(error);
    },
  });
};

export const useAssignAgentMutation = (phoneNumberId: number, options?: QueryOptions<void>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.phoneNumbers.mutations.assignAgent,
    mutationFn: ({ data }: { data: AssignAgentFormValues }) =>
      telephonyService.assignPhoneNumber(phoneNumberId, data),
    onSuccess: () => {
      notifyMutationSuccessWithOptions(
        options,
        'Agent assigned',
        'Phone number was assigned successfully.'
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.phoneNumbers.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.unassociated });
      options?.onSuccess?.();
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Assigning agent failed', error);
      options?.onError?.(error);
    },
  });
};

export const usePhoneNumberDeleteMutation = (options?: QueryOptions<void>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.phoneNumbers.mutations.delete,
    mutationFn: (phoneNumberId: number) => telephonyService.deletePhoneNumberById(phoneNumberId),
    onSuccess: () => {
      notifyMutationSuccessWithOptions(
        options,
        'Phone number deleted',
        'Phone number was deleted successfully.'
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.phoneNumbers.all });
      options?.onSuccess?.();
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Phone number deletion failed', error);
      options?.onError?.(error);
    },
  });
};

export const useFetchAllPhoneNumberQuery = (
  params: PhoneNumberFilterParams,
  options?: QueryOptions<PaginatedResponse<PhoneNumberConfiguration>>
) => {
  return useQuery<PaginatedResponse<PhoneNumberConfiguration>>({
    queryKey: queryKeys.phoneNumbers.list(params.page || 1, params.page_size, params.query || ''),
    queryFn: () => telephonyService.fetchAllPhoneNumbers(params),
    ...options,
  });
};
